'use client'

import { motion } from 'framer-motion'
import {
  HiOutlineBuildingOffice2,
  HiOutlineBriefcase,
  HiOutlineUserCircle,
} from 'react-icons/hi2'
import { useTranslations } from 'next-intl'
import Link from 'next/link'

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
      when: 'beforeChildren',
    },
  },
}

const itemVariants = {
  hidden: { y: 30, opacity: 0, scale: 0.96 },
  visible: {
    y: 0,
    opacity: 1,
    scale: 1,
    transition: {
      type: 'spring' as const,
      stiffness: 110,
      damping: 18,
    },
  },
}

const SignupChoicePage = () => {
  const t = useTranslations('auth')

  return (
    <div className="p-4 md:p-6 lg:p-8">
      <motion.div
        initial="hidden"
        animate="visible"
        variants={containerVariants}
        className="max-w-6xl mx-auto"
      >
        <motion.div
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="flex flex-col items-center mb-14"
        >
          {/* <motion.div
            initial={{ scale: 0, rotate: -90 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ type: 'spring', stiffness: 120 }}
            className="p-5 bg-[var(--color-olive-dark)] rounded-2xl shadow-lg mb-6 border border-[var(--color-gold-bright)]/40"
          >
            <HiOutlineUserCircle className="text-5xl text-[var(--color-gold-bright)]" />
          </motion.div> */}

          <h1 className="text-4xl md:text-5xl font-bold text-center text-[var(--color-dark-navy)] mb-4 font-serif">
            {t('join_platform')}
          </h1>

          <p className="text-lg text-center max-w-2xl text-[var(--color-dark-navy)]/70 leading-relaxed">
            {t('find_matches')}
          </p>
        </motion.div>

        <motion.div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
            <motion.div variants={itemVariants}>
              <Link href="/sign-up-client">
                <motion.div
                  whileHover={{ y: -8, scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="h-full cursor-pointer bg-white rounded-3xl border-2 border-[var(--color-gold-bright)]/40 shadow-lg relative overflow-hidden group"
                >
                  <div className="absolute top-0 left-0 w-full h-1 bg-[var(--color-gold-bright)] opacity-0 group-hover:opacity-100 transition-opacity" />

                  <div className="absolute inset-0 bg-[var(--color-olive-dark)] opacity-0 group-hover:opacity-5 transition-opacity" />

                  <div className="p-10 flex flex-col items-center relative z-10 text-center">
                    <motion.div
                      whileHover={{ rotate: 5, scale: 1.08 }}
                      className="p-5 mb-8 rounded-2xl bg-[var(--color-olive-dark)]/5 border border-[var(--color-gold-bright)]/30"
                    >
                      <HiOutlineBuildingOffice2 className="text-5xl text-[var(--color-gold-bright)]" />
                    </motion.div>

                    <h3 className="text-2xl font-bold text-[var(--color-dark-navy)] mb-4 font-serif">
                      {t('i_am_agency')}
                    </h3>

                    <p className="text-lg mb-8 text-[var(--color-dark-navy)]/70 leading-relaxed">
                      {t('looking_for_consultants')}
                    </p>

                    <motion.div
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="px-8 py-3 rounded-full bg-[var(--color-olive-dark)] text-white border border-[var(--color-gold-bright)] font-semibold transition-all duration-300 group-hover:bg-[var(--color-gold-bright)] group-hover:text-[var(--color-dark-navy)]"
                    >
                      {t('join_as_agency')}
                    </motion.div>
                  </div>
                </motion.div>
              </Link>
            </motion.div>

            <motion.div variants={itemVariants}>
              <Link href="/sign-up-freelancer">
                <motion.div
                  whileHover={{ y: -8, scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="h-full cursor-pointer bg-white rounded-3xl border-2 border-[var(--color-gold-bright)]/40 shadow-lg relative overflow-hidden group"
                >
                  <div className="absolute top-0 left-0 w-full h-1 bg-[var(--color-gold-bright)] opacity-0 group-hover:opacity-100 transition-opacity" />

                  <div className="absolute inset-0 bg-[var(--color-olive-dark)] opacity-0 group-hover:opacity-5 transition-opacity" />

                  <div className="p-10 flex flex-col items-center relative z-10 text-center">
                    <motion.div
                      whileHover={{ rotate: -5, scale: 1.08 }}
                      className="p-5 mb-8 rounded-2xl bg-[var(--color-olive-dark)]/5 border border-[var(--color-gold-bright)]/30"
                    >
                      <HiOutlineBriefcase className="text-5xl text-[var(--color-gold-bright)]" />
                    </motion.div>

                    <h3 className="text-2xl font-bold text-[var(--color-dark-navy)] mb-4 font-serif">
                      {t('i_am_consultant')}
                    </h3>

                    <p className="text-lg mb-8 text-[var(--color-dark-navy)]/70 leading-relaxed">
                      {t('looking_for_opportunities')}
                    </p>

                    <motion.div
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="px-8 py-3 rounded-full bg-[var(--color-olive-dark)] text-white border border-[var(--color-gold-bright)] font-semibold transition-all duration-300 group-hover:bg-[var(--color-gold-bright)] group-hover:text-[var(--color-dark-navy)]"
                    >
                      {t('join_as_consultant')}
                    </motion.div>
                  </div>
                </motion.div>
              </Link>
            </motion.div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="text-center mt-14 text-[var(--color-dark-navy)]/60"
        >
          <p className="text-sm">{t('choose_account_type')}</p>
        </motion.div>
      </motion.div>
    </div>
  )
}

export default SignupChoicePage
